import { FC, useState } from "react";
import { useController } from "react-hook-form";
import { FaQuestionCircle } from "react-icons/fa";
import { Tooltip } from "react-tooltip";
import { Select, Typography } from "antd";
import "./CBSelect.scss";
export interface OptionI {
  value: string;
  label: string;
}
interface CBSelectTypes {
  className?: string;
  label: string;
  name: string;
  control: any;
  required?: boolean;
  disabled?: boolean;
  loading?: boolean;
  searchable?: boolean;
  options?: OptionI[];
  placeholder?: string;
  tooltipMessage?: string;
  helperLink?: string;
  caption?: string;
  onBlur?: any;
  onChange?: any;
  defaultValue?: string | number | OptionI;
}

const CBSelect: FC<CBSelectTypes> = ({
  label,
  name,
  control,
  required = false,
  disabled = false,
  loading = false,
  searchable = false,
  options = [],
  placeholder = "Select...",
  tooltipMessage = "",
  helperLink = "",
  caption = "",
  onBlur = () => {},
  onChange = () => {},
  className,
  defaultValue,
}) => {
  const { field, fieldState } = useController({ name, control });
  const [requiredState, setRequiredState] = useState(false);
  const inputId = `select-input-${field.name}`;
  const labelId = `select-label-${field.name}`;
  const { Text } = Typography;
  const filterOption = (
    input: string,
    option?: { label: string; value: string },
  ) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase());
  return (
    <div className={`CBSelect my-2.5 relative ${className}`}>
      <label style={{ display: "flex" }} htmlFor={labelId}>
        {label && (
          <div className="flex gap-1">
            <Text style={{ marginBottom: 0 }}>{`${label}`}</Text>
            <Text
              type="danger"
              style={{ marginBottom: 0 }}
            >{`${required ? " *" : ""}`}</Text>
          </div>
        )}
        {helperLink && (
          <a
            style={{ textDecoration: "none" }}
            href={helperLink}
            rel="noreferrer"
            target="_blank"
          >
            (Help)
          </a>
        )}
        {tooltipMessage && (
          <>
            <span
              data-tooltip-id={`${labelId}-tooltip`}
              data-tooltip-content={tooltipMessage}
              className="tooltip"
            >
              <FaQuestionCircle />
            </span>
            <Tooltip id={`${labelId}-tooltip`} />
          </>
        )}
      </label>
      <Select
        id={inputId}
        size="large"
        disabled={disabled}
        aria-labelledby={labelId}
        aria-invalid={fieldState.error ? "true" : "false"}
        aria-disabled={disabled || loading ? "true" : "false"}
        style={{ width: "-webkit-fill-available" }}
        status={requiredState ? "error" : undefined}
        defaultValue={defaultValue ? defaultValue : ""}
        ref={field.ref}
        showSearch={searchable ?? false}
        filterOption={filterOption}
        key={field.name}
        value={field.value}
        onBlur={(e: any) => {
          field.onBlur();
          onBlur(e);
          setRequiredState(required && !field.value?.length ? true : false);
        }}
        onChange={async (e: any) => {
          field.onChange(e);
          onChange(e);
        }}
        options={options}
        placeholder={placeholder}
      />
      <div className="custom-helper">
        {caption && <Text type="secondary">{caption}</Text>}
        {!!fieldState.error && (
          <Text className="text-red-500" role="alert">
            {fieldState.error.message}
          </Text>
        )}
      </div>
    </div>
  );
};

export default CBSelect;
